import {
  useQuery,
  useMutation,
  UseQueryOptions,
  UseMutationOptions,
  QueryKey,
} from '@tanstack/react-query';

/**
 * Basic HTTP method types
 */
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

/**
 * Global API configuration used by all requests.
 * You can override any field at runtime with setApiConfig.
 */
export interface ApiConfig {
  baseUrl: string;
  /**
   * Optional function to resolve auth token or other dynamic headers.
   * Should be side‑effect free (simple sync read, e.g. localStorage).
   */
  getAuthToken?: () => string | undefined | null;
  /**
   * Extra headers added to each request (before per-request headers).
   */
  defaultHeaders?: Record<string, string>;
}

// DEFAULT CONFIG now includes a getAuthToken that reads from localStorage
const defaultConfig: ApiConfig = {
  baseUrl: process.env.REACT_APP_API_URL || '/api',
  getAuthToken: () => {
    if (typeof window === 'undefined') return undefined;
    // NOTE: adjust key name if your app uses a different storage key
    return localStorage.getItem('token') ?? undefined;
  },
};

/**
 * Allows apps to override global API configuration at runtime.
 * Example:
 *   setApiConfig({ baseUrl: 'https://example.com/api' });
 */
let runtimeConfig: Partial<ApiConfig> = {};

export function setApiConfig(config: Partial<ApiConfig>) {
  runtimeConfig = { ...runtimeConfig, ...config };
}

/**
 * Resolved configuration combining defaults and overrides.
 */
function getConfig(): ApiConfig {
  return {
    ...defaultConfig,
    ...runtimeConfig,
    defaultHeaders: {
      'Content-Type': 'application/json',
      ...(defaultConfig.defaultHeaders ?? {}),
      ...(runtimeConfig.defaultHeaders ?? {}),
    },
  };
}

/**
 * Normalized error shape returned for failed HTTP calls.
 */
export interface ApiErrorShape {
  status: number;
  message: string;
  details?: unknown;
}

/**
 * Generic, strongly-typed fetch wrapper used by all hooks.
 * - Handles baseUrl + path composition
 * - Appends query params
 * - Attaches Authorization header (if token present)
 * - Parses JSON and normalizes error shape
 */
async function apiRequest<TResponse, TBody = unknown>(
  method: HttpMethod,
  path: string,
  options?: {
    query?: Record<string, string | number | boolean | undefined | null>;
    body?: TBody;
    signal?: AbortSignal;
    headers?: Record<string, string>;
  }
): Promise<TResponse> {
  const { baseUrl, getAuthToken, defaultHeaders } = getConfig();

  // Normalize leading slash so we don't accidentally override baseUrl path
  const normalizedPath = path.replace(/^\/+/, '');
  const url = new URL(normalizedPath, baseUrl);

  if (options?.query) {
    Object.entries(options.query).forEach(([k, v]) => {
      if (v !== undefined && v !== null) url.searchParams.append(k, String(v));
    });
  }

  const headers: Record<string, string> = {
    ...defaultHeaders,
    ...(options?.headers ?? {}),
  };

  const token = getAuthToken?.();
  if (token) headers.Authorization = `Bearer ${token}`;

  // Add Accept-Language header if available
  const language = typeof window !== 'undefined' ? localStorage.getItem('language') : null;
  if (language) headers['Accept-Language'] = language;

  // Add X-Clinic-Id header if available
  const clinicId = typeof window !== 'undefined' ? localStorage.getItem('clinicId') : null;
  if (clinicId) headers['X-Clinic-Id'] = clinicId;

  const resp = await fetch(url.toString(), {
    method,
    headers,
    signal: options?.signal,
    body: options?.body ? JSON.stringify(options.body) : undefined,
  });

  const contentType = resp.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');

  if (!resp.ok) {
    const errorData = isJson
      ? await resp.json().catch(() => undefined)
      : await resp.text().catch(() => undefined);

    const error: ApiErrorShape = {
      status: resp.status,
      message:
        (isJson &&
          typeof errorData === 'object' &&
          errorData !== null &&
          'message' in errorData &&
          typeof (errorData as { message?: string }).message === 'string'
          ? (errorData as { message?: string }).message
          : undefined) ||
        resp.statusText ||
        'Request failed',
      details: errorData,
    };
    throw error;
  }

  if (!isJson) return (undefined as unknown) as TResponse;
  return (await resp.json()) as TResponse;
}

/**
 * ---------------------------------------------------------------------------
 * Common typed query/mutation helpers
 * ---------------------------------------------------------------------------
 */

export type QueryOptions<TData> = Omit<
  UseQueryOptions<TData, ApiErrorShape, TData, QueryKey>,
  'queryKey' | 'queryFn'
>;

export type MutationOptions<TData, TVariables> = Omit<
  UseMutationOptions<TData, ApiErrorShape, TVariables, unknown>,
  'mutationFn'
>;

/**
 * GET hook
 * Generic wrapper around useQuery with a simple GET request.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface GetRequestParams<TQuery extends Record<string, any> = Record<string, any>> {
  path: string;
  query?: TQuery;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useApiGet<TResponse, TQuery extends Record<string, any> = Record<string, any>>(
  key: QueryKey,
  params: GetRequestParams<TQuery>,
  options?: QueryOptions<TResponse>
) {
  return useQuery<TResponse, ApiErrorShape>({
    queryKey: key,
    queryFn: ({ signal }) =>
      apiRequest<TResponse>('GET', params.path, {
        query: params.query,
        signal,
      }),
    ...options,
  });
}

/**
 * POST hook – use for create-style mutations.
 */
export interface PostRequestParams<TBody> {
  path: string;
  body: TBody;
}

export function useApiPost<TResponse = unknown, TBody = unknown>(
  options?: MutationOptions<TResponse, PostRequestParams<TBody>>
) {
  return useMutation<TResponse, ApiErrorShape, PostRequestParams<TBody>>({
    mutationFn: ({ path, body }) => apiRequest<TResponse, TBody>('POST', path, { body }),
    ...options,
  });
}

/**
 * PUT hook – use for update-style mutations.
 */
export interface PutRequestParams<TBody> {
  path: string;
  body: TBody;
}

// Reusable helper to build PUT params, similar to POST helper pattern
export function buildPutRequestParams<TBody>(
  path: string,
  body: TBody,
): PutRequestParams<TBody> {
  return { path, body };
}

export function useApiPut<TResponse = unknown, TBody = unknown>(
  options?: MutationOptions<TResponse, PutRequestParams<TBody>>
) {
  return useMutation<TResponse, ApiErrorShape, PutRequestParams<TBody>>({
    mutationFn: ({ path, body }) => apiRequest<TResponse, TBody>('PUT', path, { body }),
    ...options,
  });
}

/**
 * DELETE hook – use for delete-style mutations.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface DeleteRequestParams<TQuery extends Record<string, any> = Record<string, any>> {
  path: string;
  query?: TQuery;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useApiDelete<TResponse = unknown, TQuery extends Record<string, any> = Record<string, any>>(
  options?: MutationOptions<TResponse, DeleteRequestParams<TQuery>>
) {
  return useMutation<TResponse, ApiErrorShape, DeleteRequestParams<TQuery>>({
    mutationFn: ({ path, query }) =>
      apiRequest<TResponse>('DELETE', path, {
        query,
      }),
    ...options,
  });
}

/**
 * Search / Filter hook (semantic GET wrapper)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface SearchRequestParams<TQuery extends Record<string, any> = Record<string, any>> {
  path: string;
  filters?: TQuery;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useApiSearch<TResponse, TQuery extends Record<string, any> = Record<string, any>>(
  key: QueryKey,
  params: SearchRequestParams<TQuery>,
  options?: QueryOptions<TResponse>
) {
  return useQuery<TResponse, ApiErrorShape>({
    queryKey: key,
    queryFn: ({ signal }) =>
      apiRequest<TResponse>('GET', params.path, {
        query: params.filters,
        signal,
      }),
    ...options,
  });
}

/**
 * Master Integration hook
 * Specialized wrapper for master-data / integration endpoints.
 */
export interface MasterIntegrationRequest<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TQuery extends Record<string, any> = Record<string, any>,
  TBody = unknown
> {
  path: string; // e.g. '/master/patients', '/integration/labs'
  query?: TQuery;
  body?: TBody;
  method?: Exclude<HttpMethod, 'DELETE'>; // usually GET/POST/PUT
}

export function useApiMasterIntegration<
  TResponse = unknown,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TQuery extends Record<string, any> = Record<string, any>,
  TBody = unknown
>(
  key: QueryKey,
  params: MasterIntegrationRequest<TQuery, TBody>,
  options?: QueryOptions<TResponse>
) {
  const method: HttpMethod = params.method ?? 'GET';

  return useQuery<TResponse, ApiErrorShape>({
    queryKey: key,
    queryFn: ({ signal }) =>
      apiRequest<TResponse, TBody>(method, params.path, {
        query: params.query,
        body: params.body,
        signal,
      }),
    ...options,
  });
}

/**
 * ---------------------------------------------------------------------------
 * Minimal demo usages for each method
 * NOTE: These are example snippets only. In real code, move them into
 *       appropriate components/files and adjust paths/types as needed.
 * ---------------------------------------------------------------------------
 */

// Example response/request types
interface Patient {
  id: number;
  name: string;
  dob: string;
}

interface CreatePatientRequest {
  name: string;
  dob: string;
}

interface UpdatePatientRequest {
  name?: string;
  dob?: string;
}

/**
 * GET Example
 */
export function useDemoGetPatient(patientId: number) {
  return useApiGet<Patient>(
    ['patient', patientId],
    { path: `/ivf/patients/${patientId}` },
    {
      enabled: !!patientId,
    }
  );
}

/**
 * POST Example (Create)
 */
export function useDemoCreatePatient() {
  return useApiPost<Patient, CreatePatientRequest>({
    onSuccess: () => {
      // e.g. invalidate queries or show toast
    },
  });
}

/**
 * PUT Example (Update)
 */
export function useDemoUpdatePatient() {
  return useApiPut<Patient, UpdatePatientRequest>({
    onSuccess: () => {
      // e.g. invalidate related queries
    },
  });
}

/**
 * DELETE Example
 */
export function useDemoDeletePatient() {
  return useApiDelete<void, { hardDelete?: boolean }>({
    onSuccess: () => {
      // e.g. invalidate list queries
    },
  });
}

/**
 * Master Integration Example
 * Example: fetch master list of clinics from an integration endpoint.
 */
interface ClinicMaster {
  id: number;
  name: string;
  city: string;
}

interface ClinicMasterQuery {
  country?: string;
}

export function useDemoMasterClinics(query: ClinicMasterQuery) {
  return useApiMasterIntegration<ClinicMaster[], ClinicMasterQuery, void>(
    ['master', 'clinics', query],
    {
      path: '/master/clinics',
      query,
      method: 'GET',
    },
    {
      staleTime: 10 * 60 * 1000, // 10 minutes
    }
  );
}

/**
 * ---------------------------------------------------------------------------
 * Example (JSX) usage patterns – keep as reference
 * ---------------------------------------------------------------------------
 *
 * // GET Patient
 * function PatientDetails({ patientId }: { patientId: number }) {
 *   const { data, isLoading, isError, error } = useDemoGetPatient(patientId);
 *   if (isLoading) return <div>Loading...</div>;
 *   if (isError) return <div>Error: {error.message}</div>;
 *   return <div>{data?.name}</div>;
 * }
 *
 * // POST Patient
 * function CreatePatientForm() {
 *   const createPatient = useDemoCreatePatient();
 *   const handleSubmit = () => {
 *     createPatient.mutate({
 *       path: '/ivf/patients',
 *       body: { name: 'New Patient', dob: '1990-01-01' },
 *     });
 *   };
 *   return (
 *     <button disabled={createPatient.isPending} onClick={handleSubmit}>
 *       {createPatient.isPending ? 'Saving...' : 'Create'}
 *     </button>
 *   );
 * }
 *
 * // PUT Patient
 * function UpdatePatientName({ id }: { id: number }) {
 *   const updatePatient = useDemoUpdatePatient();
 *   const handleUpdate = () => {
 *     updatePatient.mutate({
 *       path: `/ivf/patients/${id}`,
 *       body: { name: 'Updated Name' },
 *     });
 *   };
 *   return <button onClick={handleUpdate}>Update</button>;
 * }
 *
 * // DELETE Patient
 * function DeletePatientButton({ id }: { id: number }) {
 *   const deletePatient = useDemoDeletePatient();
 *   const handleDelete = () => {
 *     deletePatient.mutate({
 *       path: `/ivf/patients/${id}`,
 *       query: { hardDelete: false },
 *     });
 *   };
 *   return <button onClick={handleDelete}>Delete</button>;
 * }
 *
 * // Search Patients
 * function PatientSearchList({ name }: { name: string }) {
 *   const { data, isLoading } = useDemoSearchPatients({ name });
 *   if (isLoading) return <div>Searching...</div>;
 *   return (
 *     <ul>
 *       {data?.map((p) => (
 *         <li key={p.id}>{p.name}</li>
 *       ))}
 *     </ul>
 *   );
 * }
 *
 * // Master Integration – Clinics
 * function ClinicSelector() {
 *   const { data, isLoading } = useDemoMasterClinics({ country: 'IN' });
 *   if (isLoading) return <div>Loading clinics...</div>;
 *   return (
 *     <select>
 *       {data?.map((c) => (
 *         <option key={c.id} value={c.id}>
 *           {c.name} - {c.city}
 *         </option>
 *       ))}
 *     </select>
 *   );
 * }
 */
